import { useState, useEffect, useMemo } from 'react';
import {
  Trash2,
  FlaskConical,
  ChevronsUpDown,
  Check,
  ImageOff,
  Loader2,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { trpc } from '@/lib/trpc';
import { useToast } from '@/hooks/use-toast';
import { useShopifyProductSearch } from '../hooks/useLineItemExclusions';

interface LocationOption {
  id: string;
  name: string;
}

interface VariantOption {
  shopifyVariantId: string;
  sku: string;
  title: string;
  size: string | null;
  color: string | null;
}

interface PickedProduct {
  shopifyProductId: string;
  title: string;
  imageUrl: string | null;
  variants: VariantOption[];
}

interface TestOrderItemDraft {
  id: string;
  productId: string;
  productTitle: string;
  productImageUrl: string | null;
  variants: VariantOption[];
  /** null when the product has multiple variants and the admin hasn't picked yet. */
  selectedVariantId: string | null;
  quantity: number;
}

interface Props {
  open: boolean;
  onClose: () => void;
  defaultLocationId: string | null;
  locations: readonly LocationOption[];
}

/**
 * BLU-374: build a synthetic test order by picking products from Shopify.
 *
 * Multi-pick the product search and each pick becomes its own line item
 * draft. Products with more than one variant get a size/colour dropdown so
 * the synthetic order_item carries the exact SKU the admin meant to test
 * (e.g. TEE-2X-BK). Single-variant products auto-resolve.
 */
export function TestOrderModal({ open, onClose, defaultLocationId, locations }: Props) {
  const { toast } = useToast();
  const utils = trpc.useUtils();
  const [locationId, setLocationId] = useState<string>('');
  const [items, setItems] = useState<TestOrderItemDraft[]>([]);

  useEffect(() => {
    if (open) {
      setLocationId(defaultLocationId ?? '');
      setItems([]);
    }
  }, [open, defaultLocationId]);

  const createTestOrder = trpc.lineItemExclusions.createTestOrder.useMutation({
    onSuccess: (res) => {
      toast({
        title: 'Test order created',
        description: `${res.order_name} • ${res.location_name} • ${res.item_count} item${res.item_count === 1 ? '' : 's'}`,
      });
      void utils.invalidate();
      onClose();
    },
    onError: (err) => {
      toast({
        variant: 'destructive',
        title: "Couldn't create test order",
        description: err.message,
      });
    },
  });

  const canSubmit = useMemo(() => {
    if (!locationId) return false;
    if (items.length === 0) return false;
    return items.every(
      (it) => it.variants.length === 0 || it.selectedVariantId !== null,
    );
  }, [items, locationId]);

  const addProduct = (product: PickedProduct) => {
    setItems((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        productId: product.shopifyProductId,
        productTitle: product.title,
        productImageUrl: product.imageUrl,
        variants: product.variants,
        // Auto-pick if there's exactly one variant — saves the admin a click.
        selectedVariantId:
          product.variants.length === 1 ? product.variants[0]!.shopifyVariantId : null,
        quantity: 1,
      },
    ]);
  };

  const updateItem = (id: string, patch: Partial<TestOrderItemDraft>) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  };

  const removeItem = (id: string) => {
    setItems((prev) => prev.filter((it) => it.id !== id));
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    await createTestOrder.mutateAsync({
      location_id: locationId,
      items: items.map(buildPayloadItem),
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="flex max-h-[90vh] flex-col gap-0 sm:max-w-[680px]">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <FlaskConical className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            Build a test order
          </DialogTitle>
          <DialogDescription>
            Pick products from Shopify and add them to a synthetic order at the chosen location.
            The rule you just configured is applied when batch creation runs against this order.
          </DialogDescription>
        </DialogHeader>

        <div
          className="flex-1 overflow-y-auto pr-1"
          onWheel={(e) => e.stopPropagation()}
          onWheelCapture={(e) => e.stopPropagation()}
        >
          <Alert className="mb-4 border-amber-500/50 bg-amber-500/10">
            <FlaskConical className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            <AlertDescription className="text-amber-800 dark:text-amber-200">
              Test orders are flagged in the DB. They never call StarShipIt or push fulfillment
              back to Shopify: completion uses a synthetic tracking number so no fake data leaves
              the system.
            </AlertDescription>
          </Alert>

          <div className="space-y-4 py-1">
            <div className="space-y-1">
              <Label htmlFor="test-order-location">Location</Label>
              <Select value={locationId} onValueChange={(v) => setLocationId(v)}>
                <SelectTrigger id="test-order-location">
                  <SelectValue placeholder="Pick a location…" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((loc) => (
                    <SelectItem key={loc.id} value={loc.id}>
                      {loc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              <div className="space-y-1">
                <Label>Products</Label>
                <ProductPicker onPick={addProduct} />
                <p className="text-xs text-muted-foreground">
                  Pick multiple products, each one becomes a separate line item below.
                </p>
              </div>

              {items.length === 0 ? (
                <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
                  No products yet. Search above to add some.
                </div>
              ) : (
                <div className="space-y-2">
                  {items.map((item) => (
                    <DraftRow
                      key={item.id}
                      item={item}
                      onChange={(patch) => updateItem(item.id, patch)}
                      onRemove={() => removeItem(item.id)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="shrink-0 border-t bg-background pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!canSubmit || createTestOrder.isPending}
            onClick={handleSubmit}
          >
            {createTestOrder.isPending
              ? 'Creating…'
              : `Create test order (${items.length} item${items.length === 1 ? '' : 's'})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function useDebounced<T>(value: T, delayMs = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

interface ProductPickerProps {
  onPick: (product: PickedProduct) => void;
}

/**
 * Multi-pick product search. Each click adds the product as a new draft —
 * the popover stays open so the admin can keep adding without re-opening.
 * Duplicates are allowed on purpose: an admin may want two rows of the same
 * product in different sizes.
 */
function ProductPicker({ onPick }: ProductPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debounced = useDebounced(search, 250);
  const productQuery = useShopifyProductSearch(debounced, open);

  const products = productQuery.data ?? [];
  const isLoading = productQuery.isFetching;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal text-muted-foreground"
        >
          <span>Search Shopify products…</span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search by title…"
            value={search}
            onValueChange={setSearch}
          />
          <CommandList className="max-h-[320px]">
            {debounced.length < 2 ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                Type at least 2 characters
              </div>
            ) : isLoading ? (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading…
              </div>
            ) : products.length === 0 ? (
              <CommandEmpty>No products found</CommandEmpty>
            ) : (
              <CommandGroup>
                {products.map((p) => (
                  <CommandItem
                    key={p.shopifyProductId}
                    value={p.shopifyProductId}
                    onSelect={() =>
                      onPick({
                        shopifyProductId: p.shopifyProductId,
                        title: p.title,
                        imageUrl: p.imageUrl,
                        variants: p.variants,
                      })
                    }
                    className="flex items-center gap-2"
                  >
                    <Thumbnail src={p.imageUrl} alt={p.title} size="sm" />
                    <div className="flex min-w-0 flex-1 flex-col">
                      <span className="truncate text-sm">{p.title}</span>
                      <span className="truncate text-xs text-muted-foreground">
                        {p.productType ?? 'No type'} ·{' '}
                        {p.variants.length === 0
                          ? 'no variants'
                          : `${p.variants.length} variant${p.variants.length === 1 ? '' : 's'}`}
                      </span>
                    </div>
                    <Check className="h-4 w-4 shrink-0 opacity-0" />
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

interface DraftRowProps {
  item: TestOrderItemDraft;
  onChange: (patch: Partial<TestOrderItemDraft>) => void;
  onRemove: () => void;
}

function DraftRow({ item, onChange, onRemove }: DraftRowProps) {
  const showVariantPicker = item.variants.length > 1;
  const lockedVariant =
    item.variants.length === 1 ? item.variants[0]! : null;

  return (
    <div className="flex items-start gap-3 rounded-md border p-3">
      <Thumbnail src={item.productImageUrl} alt={item.productTitle} size="md" />
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium">{item.productTitle}</div>
            {lockedVariant ? (
              <div className="truncate text-xs text-muted-foreground">
                {variantLabel(lockedVariant)}
              </div>
            ) : null}
            {item.variants.length === 0 ? (
              <div className="truncate text-xs text-muted-foreground">
                No variants on Shopify, falls back to product
              </div>
            ) : null}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onRemove}
            aria-label="Remove product"
            className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex items-end gap-2">
          {showVariantPicker ? (
            <div className="min-w-0 flex-1 space-y-1">
              <Label className="text-xs text-muted-foreground">Variant</Label>
              <Select
                value={item.selectedVariantId ?? ''}
                onValueChange={(v) => onChange({ selectedVariantId: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Pick a variant…" />
                </SelectTrigger>
                <SelectContent>
                  {item.variants.map((v) => (
                    <SelectItem key={v.shopifyVariantId} value={v.shopifyVariantId}>
                      {variantLabel(v)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}
          <div className="shrink-0 space-y-1">
            <Label className="text-xs text-muted-foreground">Qty</Label>
            <Input
              type="number"
              min={1}
              max={50}
              value={item.quantity}
              onChange={(e) =>
                onChange({ quantity: Math.max(1, Number(e.target.value) || 1) })
              }
              className="w-20"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function variantLabel(v: VariantOption): string {
  const parts: string[] = [];
  if (v.size) parts.push(v.size);
  if (v.color) parts.push(v.color);
  if (parts.length === 0 && v.title && v.title !== 'Default Title') {
    parts.push(v.title);
  }
  const desc = parts.length > 0 ? parts.join(' / ') : 'Default';
  return `${desc} · ${v.sku}`;
}

function Thumbnail({
  src,
  alt,
  size,
}: {
  src: string | null | undefined;
  alt: string;
  size: 'sm' | 'md';
}) {
  const cls = size === 'md' ? 'h-12 w-12' : 'h-10 w-10';
  if (!src) {
    return (
      <span
        className={`${cls} flex shrink-0 items-center justify-center rounded-sm border border-border/50 bg-muted text-muted-foreground`}
      >
        <ImageOff className="h-4 w-4" />
      </span>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      className={`${cls} shrink-0 rounded-sm border border-border/50 bg-muted object-cover`}
      loading="lazy"
    />
  );
}

/**
 * Translate a draft into the createTestOrder payload. When a variant is
 * selected we send its real SKU + GID so the backend's
 * fetchVariantDetailsByGids resolver populates the synthetic order_item
 * with the exact size/colour metadata. Falls back to product-level resolve
 * if the product has no variants in Shopify.
 */
function buildPayloadItem(draft: TestOrderItemDraft) {
  const variant =
    draft.variants.find((v) => v.shopifyVariantId === draft.selectedVariantId) ?? null;
  return {
    sku: variant?.sku ?? 'BLU374-PRODUCT-TEST',
    product_name: draft.productTitle,
    product_type: 'Test',
    product_tags: [] as string[],
    shopify_product_id: draft.productId,
    shopify_variant_id: variant?.shopifyVariantId ?? null,
    quantity: draft.quantity,
    is_gift_card: false,
    price: 25,
  };
}
